import { ArrowsRotateIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { useInvalidateOwnedParkings } from '@repo/domain-service';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@repo/ui';

import DashboardParkingsCard from '@features/ownedParkingManage/components/dashboard/DashboardParkingsCard';
import DashboardSpotsCard from '@features/ownedParkingManage/components/dashboard/DashboardSpotsCard';

import Logo from '@shared/components/Logo';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

import parkingSign from '@assets/images/parking-sign.webp';
import whiteEllipseBackground from '@assets/images/parking-manage/white-ellipse-background.svg';

interface Props {
  testId: TestId;
}

export default function DashboardHeader({ testId }: Props) {
  const navigate = useNavigate();
  const invalidateOwnedParkings = useInvalidateOwnedParkings();
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const handleGoToParkings = () => {
    navigate(ROUTE_PATHS[RouteName.MY_PARKINGS]);
  };
  const handleGoToSpots = () => {
    navigate(ROUTE_PATHS[RouteName.MY_SPOTS]);
  };
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await invalidateOwnedParkings();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="relative">
      <div
        data-testid={testId}
        className="bg-brand-2 relative flex h-64 overflow-hidden rounded-b-2xl text-white md:rounded-2xl"
      >
        <img src={parkingSign} className="absolute -top-3 -right-5 w-18 rotate-30" alt="" aria-hidden />
        <img src={parkingSign} className="absolute bottom-6 -left-16 w-26 rotate-40 blur" alt="" aria-hidden />
        <img src={whiteEllipseBackground} className="mx-auto mt-auto w-full max-w-140" alt="" aria-hidden />
      </div>

      <div className="relative -mt-52 px-5">
        <div className="relative space-y-6">
          <Logo width={110} color="text-dark-strong" />

          <div className="flex flex-row items-center justify-between">
            <div className="text-heading">{tr('owned_parking_manage:dashboard.stats.title')}</div>
            <Button
              testId={testId.append('refresh')}
              label={tr('owned_parking_manage:dashboard.actions.refresh')}
              leftIcon={ArrowsRotateIcon}
              onClick={handleRefresh}
              pending={isRefreshing}
              color="dark"
            />
          </div>

          <div className="flex justify-between gap-6">
            <button
              type="button"
              className="flex w-1/2 cursor-pointer text-left"
              onClick={handleGoToParkings}
              aria-label={tr('owned_parking_manage:dashboard.actions.go_to_parkings')}
            >
              <DashboardParkingsCard testId={testId.append('parkings')} />
            </button>
            <button
              type="button"
              className="flex w-1/2 cursor-pointer text-left"
              onClick={handleGoToSpots}
              aria-label={tr('owned_parking_manage:dashboard.actions.go_to_spots')}
            >
              <DashboardSpotsCard testId={testId.append('spots')} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
